import { useState } from 'react'
import {
  createFileRoute,
  redirect,
  useRouteContext,
} from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import MaintenanceTaskDialog, {
  type MaintenanceScope,
  type MaintenanceTaskTypeWire,
} from '@/components/admin/MaintenanceTaskDialog'

export const Route = createFileRoute(
  '/_authenticated/_settings/user/$userId/admin/maintenance',
)({
  loader: async ({ context }) => {
    const { isSysAdmin, authState } = context
    if (!isSysAdmin) {
      throw redirect({
        to: '/user/$userId',
        params: { userId: authState!.uid },
      })
    }
  },
  component: RouteComponent,
})

type TaskMeta = {
  type: MaintenanceTaskTypeWire
  displayName: string
  description: string
}

const TASKS: TaskMeta[] = [
  {
    type: 'rag-index',
    displayName: 'Rebuild RAG Index',
    description: 'Rebuild document RAG index for chat/search.',
  },
  {
    type: 'video-embedding',
    displayName: 'Video Embedding',
    description: 'Compute missing video embeddings.',
  },
  {
    type: 'video-centroid',
    displayName: 'Video Centroid',
    description: 'Recompute video centroid vectors.',
  },
  {
    type: 'video-embedding-retry',
    displayName: 'Video Embedding Retry',
    description: 'Retry failed video embedding jobs.',
  },
  {
    type: 'metadata-backfill',
    displayName: 'Metadata Backfill',
    description: 'Extract/refresh video metadata (duration, resolution, etc.).',
  },
  {
    type: 'video-normalize-backfill',
    displayName: 'Video Normalize Backfill',
    description: 'Re-normalize videos (transcode + HLS).',
  },
  {
    type: 'blocked-job-sweep',
    displayName: 'Blocked Job Sweep',
    description:
      "Promote BLOCKED rows whose prerequisites are now satisfied but weren't auto-promoted.",
  },
]

function RouteComponent() {
  const { authState } = useRouteContext({ from: '/_authenticated' })
  const [openTask, setOpenTask] = useState<MaintenanceTaskTypeWire | null>(
    null,
  )

  // The admin route runs every task at AllTenants scope.
  const scope: MaintenanceScope = { type: 'all_tenants' }

  if (!authState) {
    // Shouldn't happen - the route loader gates on isSysAdmin which
    // requires authState. Belt-and-suspenders for the type narrowing.
    return null
  }

  return (
    <div className="settings-container">
      <h1 className="text-2xl">System Maintenance</h1>
      <p className="text-copy-secondary max-w-3xl text-sm">
        Run cluster-wide maintenance tasks across every tenant. All tasks are
        scheduled as background jobs via the worker pool.
      </p>

      <div className="border-warning bg-warning/10 mt-2 rounded-md border px-3 py-2 text-sm">
        <strong>Warning: </strong>
        These tasks run for every tenant on the cluster. Double-check the
        options before submitting.
      </div>

      <div className="mt-4 flex flex-row flex-wrap gap-3">
        {TASKS.map((task) => (
          <div
            key={task.type}
            className="bg-card flex max-w-[280px] flex-col gap-1 rounded-md border p-3"
          >
            <Button
              variant={
                task.type === 'video-normalize-backfill'
                  ? 'destructive'
                  : 'default'
              }
              onClick={() => setOpenTask(task.type)}
            >
              {task.displayName}
            </Button>
            <span className="text-copy-secondary text-sm">
              {task.description}
            </span>
            {task.type === 'video-normalize-backfill' ? (
              <span className="text-destructive text-sm">
                Warning: with clearFirst on, also deletes transcoded HLS files
                from GCS.
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {TASKS.map((task) => (
        <MaintenanceTaskDialog
          key={task.type}
          open={openTask === task.type}
          onOpenChange={(next) => {
            if (!next && openTask === task.type) {
              setOpenTask(null)
            }
          }}
          authState={authState}
          scope={scope}
          taskType={task.type}
          description={task.description}
          destructiveWarning={
            task.type === 'video-normalize-backfill'
              ? 'Re-normalize will transcode every matching video. With clearFirst on, transcoded HLS files are deleted from GCS first.'
              : undefined
          }
        />
      ))}
    </div>
  )
}
